module.exports = { ledgerBalanceToReport }

const cloneDeep = require('lodash/cloneDeep')
const { REPORT_BALANCE_FIELD_SHORT } = require('./utils/constants')
const { getVersion } = require('../utils/versions')
const toStockCountId = require('./to-stock-count-id')
const docToStockCountRecord = require('./doc-to-stock-count-record')
const getPeriodFromProgram = require('../report/tools/get-period-from-program')
const shouldTrackBatches = require('./should-track-batches')

function copyOpenings (stockPointer) {
  Object.keys(stockPointer).forEach(productId => {
    const fields = stockPointer[productId].fields || {}
    if (fields['field:partner-balance']) {
      fields['field:opening-partner-balance'] = cloneDeep(fields['field:partner-balance'])
    }

    if (fields['field:standard-physical-count']) {
      fields['field:standard-opening-balance'] = cloneDeep(fields['field:standard-physical-count'])
    }
  })
}

function ledgerBalanceToReport (params) {
  const { ledger, location, service, date, user, asDocument = false, fields = [], calculateFields = false } = params
  const ledgerFields = fields.filter(f => f.ledger === true)
  const reportingPeriod = getPeriodFromProgram(service.program, date, true)

  const _id = toStockCountId({ location: location.id, service, reportingPeriod: reportingPeriod.id })
  const version = getVersion(service.id)

  // For Shelflife we want to preserve product-level counts and not overwrite them
  // with the sum of batch quantities since for SL warehouses we might not know the total batches of a product
  // Default behaviour (other programs) still aggregates batch totals.
  let skipBatchAggregation = false
  if (location && location.level === 'pack-point') {
    skipBatchAggregation = true
  }

  const stock = getStockReportStock(ledger, location, service, ledgerFields, skipBatchAggregation)
  const timestamp = new Date().toISOString()
  const report = {
    _id,
    version,
    stock,
    type: 'stockCount',
    serviceId: service.id,
    createdAt: timestamp,
    createdBy: user.name,
    updatedAt: timestamp,
    updatedBy: user.name,
    submittedAt: timestamp
  }

  let docParams = { skipBatchAggregation }
  if (calculateFields) {
    // Copy partner-balance to opening-partner-balance so the other fields are calculated correctly
    copyOpenings(report.stock)
    docParams = { ...docParams, fields, fieldOpts: { tracksPartnerBalances: location.tracksPartnerBalances } }
  }

  return asDocument
    ? report
    : docToStockCountRecord(report, service, docParams)
}

function getStockReportStock (ledger, location, service, fields, skipBatchAggregation) {
  // These are already the relevant products for our location:service.
  const areBatchesTracked = shouldTrackBatches({
    service,
    location
  })

  return Object.keys(ledger).reduce((memo, productId) => {
    const { batches, total, commits, skipCount } = ledger[productId]
    if (areBatchesTracked) {
      memo[productId] = { batches: {} }
      for (let batchId in batches) {
        const batchTotal = batches[batchId]
        memo[productId].batches[batchId] = {
          fields: { [REPORT_BALANCE_FIELD_SHORT]: { amount: skipBatchAggregation ? total : batchTotal } }
        }
      }

      if (skipBatchAggregation) {
        memo[productId].fields = {
          [REPORT_BALANCE_FIELD_SHORT]: { amount: total }
        }

        fields.forEach(field => {
          const id = field.id
          const amount = ledger[productId][id]
          memo[productId].fields[id] = { amount }
        })
      }
    } else {
      memo[productId] = {
        fields: {
          [REPORT_BALANCE_FIELD_SHORT]: { amount: total }
        }
      }

      fields.forEach(field => {
        const id = field.id
        const amount = ledger[productId][id]
        memo[productId].fields[id] = { amount }
      })
    }
    if (commits) {
      memo[productId].commits = cloneDeep(commits)
    }

    if (skipCount) {
      memo[productId].skipCount = true
    }

    return memo
  }, {})
}
